export const rarityXpMultiplier = {
    Common: 10,
    Uncommon: 20,
    Rare: 50,
    'High Rare': 100,
    'Super Rare': 200,
    'Super Super Rare': 400,
    Legendary: 800,
    Mythic: 1600
};
export const getMaxLevel = (rarity) => {
    return {
        Common: 20,
        Uncommon: 30,
        Rare: 40,
        'High Rare': 50,
        'Super Rare': 60,
        'Super Super Rare': 70,
        Legendary: 80,
        Mythic: 100
    }[rarity] || 20;
};

export const getXpForLevel = (level) => {
    switch (level) {
        case 1: return 10;
        case 2: return 20;
        case 3: return 40;
        case 4: return 70;
        case 5: return 110;
        case 6: return 160;
        case 7: return 220;
        case 8: return 290;
        case 9: return 370;
        default: return 450 + (level - 10) * 100; // Scale beyond 10
    }
};

export const getAscensionCost = (rarity, ascensions = 0) => {
    const baseCosts = {
        Common: 100,
        Uncommon: 250,
        Rare: 500,
        'High Rare': 1000,
        'Super Rare': 2000,
        'Super Super Rare': 3500,
        Legendary: 5000
    };
    const base = baseCosts[rarity] ?? 100;
    const growthRate = 1.75; // cost grows per ascension already performed
    return Math.round(base * Math.pow(growthRate, ascensions));
};


export const getCardStats = (instance, baseCard) => {
    if (!instance || !baseCard) return { attack: 0, defense: 0 };

    const evolutionData = baseCard.evolutions?.[instance.evolutionStage] || {};
    const isEvolved = instance.evolutionStage >= 2;

    const attackGrowth = baseCard.attackGrowth ?? 0;
    const defenseGrowth = baseCard.defenseGrowth ?? 0;

    const bonusPerLevelAttack = isEvolved ? (evolutionData.attackBonus ?? 0) : 0;
    const bonusPerLevelDefense = isEvolved ? (evolutionData.defenseBonus ?? 0) : 0;

    const level = instance.level ?? 1;
    const effectiveLevels = Math.max(level - 1, 0);

    const attack = Math.floor(baseCard.baseStats.attack + effectiveLevels * (attackGrowth + bonusPerLevelAttack));
    const defense = Math.floor(baseCard.baseStats.defense + effectiveLevels * (defenseGrowth + bonusPerLevelDefense));

    return { attack, defense };
};

export const applyAbilities = (sourceCards, targetCards) => {
    // deep clones
    const modifiedTargets = JSON.parse(JSON.stringify(targetCards));
    const modifiedSelf = JSON.parse(JSON.stringify(sourceCards));

    // helper: coerce stats to numbers (avoid NaN cascades)
    const num = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    // normalize class strings once
    const norm = (s) => String(s || '').trim().toLowerCase();

    // Build quick lookup arrays we’ll mutate
    // Ensure every card has numeric attack/defense fields
    for (const c of modifiedSelf) {
        c.attack = num(c.attack);
        c.defense = num(c.defense);
    }
    for (const c of modifiedTargets) {
        c.attack = num(c.attack);
        c.defense = num(c.defense);
    }

    sourceCards.forEach((originalSource, sourceIdx) => {
        if (!originalSource?.triggered || !Array.isArray(originalSource.ability)) return;

        // Find the *corresponding* source card inside modifiedSelf
        const source = modifiedSelf[sourceIdx];
        if (!source) return;

        originalSource.ability.forEach((effect) => {
            if (!effect?.target || (!effect.increase && !effect.decrease)) return;

            // Parse "Beast.attack" safely
            const parts = String(effect.target).split('.');
            if (parts.length !== 2) return;
            const targetClassNorm = norm(parts[0]);
            const targetStat = parts[1] === 'attack' ? 'attack' : parts[1] === 'defense' ? 'defense' : null;
            if (!targetStat) return;

            // choose side
            const applyTo = effect.targetType === 'self' ? modifiedSelf : modifiedTargets;

            for (let i = 0; i < applyTo.length; i++) {
                const target = applyTo[i];
                if (norm(target.classification) !== targetClassNorm) continue;

                // base value *at the moment of application*
                const baseVal = num(target[targetStat]);
                const flat = num(effect.flatAmount);
                const pct = num(effect.percent);

                const percentPart = pct ? (baseVal * (pct / 100)) : 0;
                const delta = Math.floor(flat + percentPart);

                if (!delta) continue;

                if (effect.increase) {
                    target[targetStat] = baseVal + delta;
                } else if (effect.decrease) {
                    target[targetStat] = Math.max(0, baseVal - delta);
                }
            }
        });
    });

    return {
        modifiedSelf,
        modifiedTargets
    };
};

/**
 * Merge a user card instance with its base card and evolution overlay,
 * and also account for ascensions (extra level cap, ascended flag, etc).
 *
 * @param {Object} instance - The user’s owned card instance (from Firestore).
 * @param {Object} globalCards - The global card dictionary (id -> base card).
 * @returns {Object|null} Hydrated card object ready for display.
 */
export const hydrateCard = (instance, globalCards) => {
    if (!instance || !globalCards) return null;

    const base = globalCards[instance.cardId];
    if (!base) return null;

    const evolutionStage = Number(instance.evolutionStage || 0);
    const evo = base.evolutions?.[evolutionStage] || {};

    // Ascensions: default to 0 if missing
    const ascensions = Number(instance.ascensions ?? 0);

    // Rarity can change with evolution
    const rarity = evo.rarity || base.rarity || 'Common';

    // Base max level from rarity (preferred) or from base card if you store it there.
    // Assumes `getMaxLevel(rarity)` is available in this module; if not,
    // you can replace this with your own lookup or remove baseMaxLevel/levelCap.
    let baseMaxLevel;
    if (typeof getMaxLevel === 'function') {
        baseMaxLevel = getMaxLevel(rarity);
    } else if (typeof base.maxLevel === 'number') {
        baseMaxLevel = base.maxLevel;
    } else {
        baseMaxLevel = undefined;
    }

    // Current level cap INCLUDING ascensions: baseMax + ascensions * 10
    const levelCap =
        typeof baseMaxLevel === 'number'
            ? baseMaxLevel + ascensions * 10
            : undefined;

    return {
        // Base card data
        ...base,
        // Instance data (level, xp, evolutionStage, ascensions, active flags, etc)
        ...instance,

        // Normalized ascension-related fields
        ascensions,
        isAscended: ascensions > 0,

        // Evolution overlays
        image: evo.image || base.image,
        rarity,
        ability: evo.ability || base.ability,
        abilityDescription: evo.abilityDescription || base.abilityDescription,

        // Level cap info for UI / logic
        baseMaxLevel,
        levelCap,
    };
};

/**
 * Hydrate an array of instances at once (now including ascension info).
 */
export const hydrateInstances = (instances = [], globalCards = {}) =>
    instances
        .map((inst) => hydrateCard(inst, globalCards))
        .filter(Boolean);


// utilities/trophies.js
export function computeTrophyDelta(opts) {
    const MIN = 5;
    const MAX = 33;
    const RANGE = MAX - MIN;
    const MID = MIN + RANGE / 2; // 19

    // Weights for how much trophies vs deck power matter
    const ALPHA_TROPHIES = 0.6;
    const ALPHA_POWER = 0.4;

    // How quickly advantage saturates
    const TROPHY_SCALE = 300; // ~300 trophy lead ≈ clear favorite
    const POWER_MULT = 2;     // tanh multiplier for power gap

    const wt = Math.max(0, opts.winnerTrophies | 0);
    const lt = Math.max(0, opts.loserTrophies | 0);
    const wp = Math.max(1, opts.winnerPower | 0);
    const lp = Math.max(1, opts.loserPower | 0);

    // Positive = winner was favorite, negative = winner was underdog
    const trophyAdv = Math.tanh((wt - lt) / TROPHY_SCALE); // [-1, 1]
    const denom = Math.max(wp, lp, 1);
    const relPower = (wp - lp) / denom;                    // ~[-1, 1]
    const powerAdv = Math.tanh(POWER_MULT * relPower);     // [-1, 1]

    const favoriteScore = ALPHA_TROPHIES * trophyAdv + ALPHA_POWER * powerAdv;

    // Map favoriteScore ∈ [-1,1] to delta ∈ [MAX when underdog, MIN when favorite]
    const normalized = (favoriteScore + 1) / 2;            // [0,1]
    const expectedGain = MID - (normalized - 0.5) * RANGE;

    const delta = Math.max(MIN, Math.min(MAX, Math.round(expectedGain)));
    return delta;
}
